import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../lib/api.js';
import { useSession } from '../state/session.js';
import { ErrorBox } from '../components/ui.js';

export function NewProjectPage() {
  const navigate = useNavigate();
  const { refreshProjects } = useSession();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [params] = useSearchParams();
  const [isPrivate, setIsPrivate] = useState(params.get('private') === '1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = description.trim() ? { name: name.trim(), description: description.trim() } : { name: name.trim() };
      const { project } = await api.projects.create({ ...body, ...(isPrivate ? { private: true } : {}) });
      await refreshProjects();
      navigate(`/p/${project.id}`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page narrow">
      <h1>New project</h1>
      <p className="page-subtitle muted small">
        The description becomes project context v1. You can edit and publish new versions later; every conversation records the
        version it used.
      </p>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      <form onSubmit={(e) => void submit(e)} className="stack">
        <label className="field" htmlFor="name">
          <span>Name</span>
          <input id="name" required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="field" htmlFor="desc">
          <span>Project context (markdown, optional)</span>
          <textarea
            id="desc"
            rows={10}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What the project is, who it is for, what decision it serves, what is already decided..."
          />
        </label>
        <label className="row small">
          <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} /> Private: only I can see it until I share it
        </label>
        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
            Create project
          </button>
        </div>
      </form>
    </div>
  );
}
